import { EChartsOption } from 'echarts';
import { AHcurrOpModeEnum, AHglobStatusEnum, DetailsSeries, getStyleProp, toFixedIfNecessary } from 'ngx-core-services';

import { ChartAttributes } from '.';

/**
 * Attribute ids supported for Heat Pump charting
 */
export type HpAttributeId =
  | 'OUT_TEMP'
  | 'RET_TEMP'
  | 'DHW_TEMP'
  | 'HP_E_PWR'
  | 'HP_E_EG'
  | 'HP_E_ET'
  | 'HP_T_PWR'
  | 'HP_T_EG'
  | 'ENV_TEMP'
  | 'HP_T_ET';

/** Minimal telemetry shape we need for charting */
export type HeatPumpSample = Partial<{
  TS: number | string;
  OUT_TEMP: number;
  RET_TEMP: number;
  DHW_TEMP: number;
  HP_E_PWR: number;
  HP_E_EG: number;
  HP_E_ET: number;
  HP_T_PWR: number;
  HP_T_EG: number;
  HP_T_ET: number;
  globStatus: AHglobStatusEnum;
  CURR_OP_MODE: AHcurrOpModeEnum;
  __typename: string;
}>;

export const HP_ATTR_COLOR_MAP: Record<HpAttributeId, string> = {
  // Temperature (warm ramp: giallo → arancione → rosso)
  OUT_TEMP:  getStyleProp('--red-400'),
  RET_TEMP:  getStyleProp('--orange-300'),
  DHW_TEMP:  getStyleProp('--red-600'),
  ENV_TEMP:  getStyleProp('--orange-500'),
  // Electric power/energy
  HP_E_PWR:  getStyleProp('--purple-600'),
  HP_E_EG:   getStyleProp('--blue-500'),
  HP_E_ET:   getStyleProp('--blue-700'),
  // Thermal power/energy equivalent
  HP_T_PWR:  getStyleProp('--green-600'),
  HP_T_EG:   getStyleProp('--ottanio-500'),
  HP_T_ET:   getStyleProp('--ottanio-700'),
};

/**
 * Convert raw heat pump samples into DetailsSeries[] for selected attributes.
 * Uses the same midday-centered X positioning used by power charts.
 */
export const buildHeatPumpDailySeries = (
  samples: HeatPumpSample[],
  date: string,
  tz: string,
  selectedAttrIds: HpAttributeId[],
): DetailsSeries[] => {
  // compute midday reference in ms like power chart util
  const day = new Date(date);
  day.setHours(day.getHours() - day.getTimezoneOffset() / 60);
  day.setHours(12 - day.getTimezoneOffset() / 60 - Number(tz));
  const midDay = day.getTime();

  // Prepare accumulator for each selected attribute
  const acc: Record<string, { name: string; values: [number, number][] }> = {};
  for (const id of selectedAttrIds) {
    acc[id] = { name: id, values: [] };
  }

  // Fill points
  for (const row of samples) {
    const ts = Number(row.TS) * 1000;
    if (!isFinite(ts)) continue;
    const x = Math.floor((ts - midDay) / 1000);

    for (const id of selectedAttrIds) {
      const raw = (row as any)[id] as number | undefined;
      if (typeof raw === 'number' && isFinite(raw)) {
        acc[id].values.push([x, raw]);
      }
    }
  }

  // Sort by x asc and return compact array
  const series: DetailsSeries[] = Object.values(acc).map(s => ({
    name: s.name,
    values: s.values.sort((a, b) => (a[0] <= b[0] ? -1 : 1)),
  }));

  return series;
};

export const appBuildHeatPumpDailyGraphOptions = (
  series: DetailsSeries[],
  chartAttributes: ChartAttributes[],
  labels: Record<string, string>,
  samples?: HeatPumpSample[],
  date?: string,
  tz?: string,
): EChartsOption => {
  // Filter only series that have a chartAttribute
  series = series.filter(s => chartAttributes.some(attr => attr.id === s.name));

  const attributesMap = new Map(chartAttributes.map(attr => [attr.id, attr] as const));

  // Build yAxis per unit (first unit on LEFT, others on RIGHT with columns)
  const yAxisOptionsMap = new Map<string, number>();
  const yAxis: any[] = [];

  // Preserve unit discovery order based on chartAttributes sequence
  chartAttributes.forEach(attr => {
    const unit = attr.attribute.graphOpts.unit;
    if (!yAxisOptionsMap.has(unit)) {
      const yAxisIndex = yAxis.length;
      yAxisOptionsMap.set(unit, yAxisIndex);
      yAxis.push({
        type: 'value',
        name: unit,
        nameLocation: 'end',
        nameTextStyle: {
          color: getStyleProp('--grey-500'),
          fontWeight: 'bold',
        },
        position: yAxisIndex === 0 ? 'left' : 'right',
        offset: 0, // will be spaced for right-side units below
        splitLine: { show: false },
        axisLine: { show: true, onZero: false },
        axisTick: { show: true },
        axisLabel: {
          show: true,
          hideOverlap: true,
          margin: 4,
          showMinLabel: false,
          showMaxLabel: false,
          formatter: (value: any) => toFixedIfNecessary(value, 2).toString(),
        },
      });
    }
  });

  const axisStep = 40;
  let rightCol = 0;
  yAxis.forEach((ax: any) => {
    if (ax.position === 'right') {
      ax.offset = rightCol * axisStep;
      rightCol += 1;
    }
  });

  // Build xAxis
  const xAxis = {
    max: 12 * 60 * 60,
    min: -12 * 60 * 60,
    maxInterval: 12 * 60 * 60 * 2,
    type: 'value',
    axisLine: { show: true },
    axisTick: { show: true },
    splitLine: { show: false },
    axisLabel: {
      show: true,
      hideOverlap: true,
      showMinLabel: false,
      showMaxLabel: false,
      formatter: (value: number) => {
        const hour = Math.round(value / 3600) + 12;
        return `${hour < 10 ? '0' : ''}${hour}`;
      },
    },
  } as const;

  // --- Inline helpers for sessions & status (scoped to this builder) ---
  const computeMidDay = (d: string, tzStr: string) => {
    const day = new Date(d);
    day.setHours(day.getHours() - day.getTimezoneOffset() / 60);
    day.setHours(12 - day.getTimezoneOffset() / 60 - Number(tzStr));
    return day.getTime();
  };

  let activeRanges: Array<[number, number]> = [];
  let statusSegs: Array<{ from: number; to: number; status: AHglobStatusEnum | undefined }> = [];
  let currOpModeSegs: Array<{ from: number; to: number; mode: AHcurrOpModeEnum | undefined }> = [];

  if (samples && samples.length && date && tz !== undefined) {
    const midDay = computeMidDay(date, tz);
    const rows = [...samples]
      .filter(r => r && r.TS != null)
      .sort((a, b) => Number(a.TS) - Number(b.TS));

    // Build active ranges (globStatus != Standby/Shutdown)
    let inActive = false;
    let startX: number | null = null;
    const isActiveStatus = (st: any) => (st !== AHglobStatusEnum.Standby && st !== AHglobStatusEnum.Shutdown);
    for (let i = 0; i < rows.length; i++) {
      const ts = Number(rows[i].TS) * 1000; if (!isFinite(ts)) continue;
      const x = Math.floor((ts - midDay) / 1000);
      const active = isActiveStatus((rows[i] as any).globStatus);
      if (active && !inActive) { inActive = true; startX = x; }
      else if (!active && inActive) { const endX = x; if (startX != null && isFinite(endX)) activeRanges.push([startX, endX]); inActive = false; startX = null; }
    }
    if (inActive && startX != null) {
      const last = rows[rows.length - 1];
      const endX = Math.floor((Number(last.TS) * 1000 - midDay) / 1000);
      activeRanges.push([startX, endX]);
    }

    // Build status & mode segments
    if (rows.length) {
      let currentStatus: AHglobStatusEnum | undefined = rows[0].globStatus as any;
      let statusSegStart = Math.floor((Number(rows[0].TS) * 1000 - midDay) / 1000);

      let currentMode: AHcurrOpModeEnum | undefined = rows[0].CURR_OP_MODE as any;
      let modeSegStart = statusSegStart;

      for (let i = 1; i < rows.length; i++) {
        const x = Math.floor((Number(rows[i].TS) * 1000 - midDay) / 1000);

        const st = rows[i].globStatus as any;
        if (st !== currentStatus) {
          statusSegs.push({ from: statusSegStart, to: x, status: currentStatus });
          currentStatus = st;
          statusSegStart = x;
        }

        const mode = rows[i].CURR_OP_MODE as any;
        if (mode !== currentMode) {
          currOpModeSegs.push({ from: modeSegStart, to: x, mode: currentMode });
          currentMode = mode;
          modeSegStart = x;
        }
      }

      const lastX = Math.floor((Number(rows[rows.length - 1].TS) * 1000 - midDay) / 1000);
      statusSegs.push({ from: statusSegStart, to: lastX, status: currentStatus });
      currOpModeSegs.push({ from: modeSegStart, to: lastX, mode: currentMode });
    }
  }

  // Tooltip with labels map and sign rules, includes status chip if available
  const tooltip = {
    confine: true,
    trigger: 'axis',
    formatter: (elements: any): string => {
      if (!elements || !elements.length) return '';
      const { data: [valuex] } = elements[0];
      const hour = Math.floor((valuex + (12 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor(((valuex + (12 * 60 * 60)) / 60) % 60);

      const statusAt = (x: number): AHglobStatusEnum | undefined => {
        for (let i = 0; i < statusSegs.length; i++) {
          const s = statusSegs[i];
          if (x >= s.from && x <= s.to) return s.status;
        }
        return;
      };
      const currOpModeAt = (x: number): AHcurrOpModeEnum | undefined => {
        for (let i = 0; i < currOpModeSegs.length; i++) {
          const s = currOpModeSegs[i];
          if (x >= s.from && x <= s.to) return s.mode;
        }
        return;
      };
      const status = statusAt(valuex);
      const mode = currOpModeAt(valuex);
      const headerColor = status !== AHglobStatusEnum.Standby && status !== AHglobStatusEnum.Shutdown ? 'text-green-500 bg-green-100' : 'text-accent-700 bg-accent-100';
      const modeColor = mode === AHcurrOpModeEnum.Cooling ? 'text-blue-500 bg-blue-100' : mode === AHcurrOpModeEnum.Heating ? 'text-orange-500 bg-orange-100' : 'text-orange-500 bg-orange-100';

      const headerTemplate = elements.length > 0 ? `
          <div class="fx-row fx-align-center fx-justify-between tw-text-sm p-1 ${headerColor} radius-t-4">
            <div class="tw-font-sm mr-auto pl-2"><span class="tw-text-xs tw-font-bold">${status ? labels[status] : ''}</span></div>
            <div class="tw-font-sm ml-auto">${hour}:${minutes <= 9 ? ('0' + minutes) : minutes} <span class="tw-text-xs tw-font-bold">${hour < 12 ? 'AM' : 'PM'}</span></div>
          </div>
          <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 ${modeColor} p-1">
          <div class="tw-font-sm mr-auto pl-2"><span class="tw-text-xs tw-font-bold"> ${labels['modeTitle'] ?? 'Mode'}</span></div>
            <div class="tw-font-normal">${mode !== undefined ? (labels[mode] ?? mode) : ''}</div>
          </div>
        ` : '';

      const detailsTemplate = elements.map((element: any) => {
        const { seriesName, data: [_, valuey], color } = element;
        const attr = attributesMap.get(seriesName);
        const useSignLabel = attr?.attribute.graphOpts.sign;
        const name = useSignLabel
          ? valuey > 0
            ? useSignLabel.positive
            : useSignLabel.negative
          : seriesName;
        const unit = attr?.attribute.graphOpts.unit || '';
        const value = useSignLabel ? Math.abs(valuey) : valuey;
        return `
          <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
            <div class="tw-font-medium text-style-italic mr-5 fx-row">
              <div class="s-4 rounded mr-3" style="background: ${color}"></div>
              ${labels[name] ?? name}
            </div>
            <div class="tw-font-normal">${toFixedIfNecessary(`${value}`, 2).toString() } <span class="tw-text-xs tw-font-bold">${unit}</span></div>
          </div>`;
      }).join('');

      return `${headerTemplate}${detailsTemplate}`;
    },
    backgroundColor: 'var(--white-100)',
    borderColor: 'var(--grey-300)',
    shadowBlur: 0,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    borderRadius: 4,
    padding: 0,
    textStyle: { color: 'var(--white)' },
  };

  // Series mapping
  const mappedSeries = series.map(s => {
    const id = s.name as HpAttributeId;
    const attr = attributesMap.get(s.name);
    const unit = attr?.attribute.graphOpts.unit || '';
    const yAxisIndex = yAxisOptionsMap.get(unit) || 0;
    const transformUnitFn = attr?.attribute.graphOpts.transformUnitFn;

    // Fixed color per grandezza (fallback al colore dell'attributo se non mappato)
    const fixedColor = HP_ATTR_COLOR_MAP[id] || attr?.attribute.graphOpts.color;

    return ({
      name: s.name,
      type: 'line',
      smooth: true,
      color: fixedColor,
      symbol: 'none',
      yAxisIndex,
      xAxisIndex: 0,
      data: s.values.map(([time, value]) => [time, transformUnitFn ? transformUnitFn(value) : value]),
    });
  });

  // Legend must list only the actual user-selected plotted series (exclude bg)
  const legendData = mappedSeries.map(s => s.name);

  // Add background sessions if available
  if (activeRanges.length) {
    const bgSeries = {
      name: '__active_bg__',
      type: 'line',
      data: [],
      yAxisIndex: 0,
      xAxisIndex: 0,
      silent: true,
      tooltip: { show: false },
      lineStyle: { opacity: 0 },
      markArea: {
        silent: true,
        itemStyle: { color: getStyleProp('--green-100') },
        data: activeRanges.map(([from, to]) => [{ xAxis: from }, { xAxis: to }]),
      },
      z: -1,
      zlevel: 0,
    } as any;
    (mappedSeries as any).unshift(bgSeries);
  }

  return {
    grid: {
      left: 5,
      right: 20,
      top: 40,
      bottom: 10,
      containLabel: true,
    },
    legend: {
      show: false,
      type: 'scroll',
      bottom: 4,
      left: 'center',
      orient: 'horizontal',
      selectedMode: 'multiple',
      icon: 'roundRect',
      itemWidth: 18,
      itemHeight: 10,
      data: legendData,
      textStyle: { color: getStyleProp('--grey-500') },
      formatter: (name: string) => labels[name] ?? name,
    },
    tooltip,
    xAxis,
    yAxis,
    series: mappedSeries,
  } as EChartsOption;
};

/**
 * Build an ECharts option for heat pump telemetry based on the selected attributes
 * and the generic daily graph builder used across power charts, reusing units/colors
 * provided via `chartAttributes`.
 */

export const appBuildHeatPumpDailyGraph = (
  samples: HeatPumpSample[],
  date: string,
  tz: string,
  chartAttributes: ChartAttributes[],
  labels: Record<string, string>,
  selectedAttrIds: HpAttributeId[],
): EChartsOption => {
  const series = buildHeatPumpDailySeries(samples, date, tz, selectedAttrIds);
  return appBuildHeatPumpDailyGraphOptions(series as any, chartAttributes, labels, samples, date, tz) as any;
};

/**
 * Build a grouped histogram (multi-bar) for Heat Pump energy aggregations.
 * Works with the aggregated data produced in the heatpump-energy-aggregation component,
 * where each daily item may contain a `rawData` object with HP_* energy keys.
 */
export const appBuildHeatPumpEnergyAggregationMultiBar = (
  aggregatedData: {
    period: 'month' | 'year';
    dailyData: Array<{ date: string; rawData?: any | null }>; // rawData may host HP_* energy fields
  },
  labels: Record<string, string>,
  energyAttrs: HpAttributeId[] = ['HP_E_EG', 'HP_T_EG']
): EChartsOption => {
  // Fixed, positive-looking colors (avoid red):
  const FIXED_ENERGY_COLORS: Record<string, string> = {
    HP_E_EG: getStyleProp('--blue-500'), // Electric energy consumed -> blue
    HP_T_EG: getStyleProp('--orange-500'),
  };

  const isMonthMode = aggregatedData.period === 'month';
  const daysOrMonths = aggregatedData.dailyData.map(d => new Date(d.date));

  // X categories (day index in month, or month short name)
  const xCategories = daysOrMonths.map(dt =>
    isMonthMode ? `${dt.getDate()}` : dt.toLocaleDateString('en-US', { month: 'short' })
  );

  // Series: one bar series per energy attribute
  const series = energyAttrs.map(attrId => {
    const values = aggregatedData.dailyData.map(d => {
      const row = d.rawData || {};
      const v = Number((row as any)[attrId] ?? 0);
      return isFinite(v) ? v : 0;
    });

    return {
      name:
        labels[attrId] ??
        (attrId === 'HP_E_EG'
          ? 'Energia elettrica consumata'
          : attrId === 'HP_T_EG'
          ? 'Energia termica equivalente'
          : attrId),
      type: 'bar',
      data: values,
      itemStyle: { color: FIXED_ENERGY_COLORS[attrId] || HP_ATTR_COLOR_MAP[attrId] || getStyleProp('--grey-500') },
      // Lighter/disabled hover: no focus, no dimming of other bars
      emphasis: { focus: 'none', itemStyle: { opacity: 1 } },
      blur: { itemStyle: { opacity: 1 } },
      barGap: '10%',
      barWidth: '45%',
    } as any;
  });

  return {
    grid: { left: 10, right: 10, bottom: 8, top: 40, containLabel: true },
    tooltip: {
      confine: true,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      // Same visual style as the daily chart tooltip
      backgroundColor: 'var(--white-100)',
      borderColor: 'var(--grey-300)',
      shadowBlur: 0,
      shadowOffsetX: 0,
      shadowOffsetY: 0,
      borderRadius: 4,
      padding: 0,
      textStyle: { color: 'var(--white)' },
      formatter: (elements: any): string => {
        if (!elements || !elements.length) return '';
        const idx = elements[0].dataIndex;
        const dt = daysOrMonths[idx];
        const formattedDate = isMonthMode
          ? `${dt.getDate().toString().padStart(2, '0')}/${(dt.getMonth() + 1).toString().padStart(2, '0')}`
          : dt.toLocaleDateString('en-US', { month: 'short' });

        const headerTemplate = `
          <div class="fx-row text-accent-700 fx-align-center fx-justify-between tw-text-sm p-1 bg-accent-100 radius-t-4">
            <div class="tw-font-sm ml-auto">${formattedDate}</div>
          </div>
        `;

        const detailRows = elements.map((el: any) => {
          const { seriesName, color, data } = el;
          const label = labels[seriesName] ?? seriesName;
          const unit = 'kWh';
          const value = Number(data) || 0;
          return `
            <div class="fx-row fx-align-center fx-justify-between tw-text-sm text-grey-400 p-1">
              <div class="tw-font-medium text-style-italic mr-5 fx-row">
                <div class="s-4 rounded mr-3" style="background: ${color}"></div>
                ${label}
              </div>
              <div class="tw-font-normal">${toFixedIfNecessary(`${value}`, 2).toString() } <span class="tw-text-xs tw-font-bold">${unit}</span></div>
            </div>
          `;
        }).join('');

        return `${headerTemplate}${detailRows}`;
      },
    },
    xAxis: {
      type: 'category',
      data: xCategories,
      axisLine: { onZero: true, show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        color: getStyleProp('--grey-300'),
        fontWeight: 'bold',
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: true,
        rotate: 45,
      },
    },
    yAxis: {
      type: 'value',
      name: 'kWh',
      nameLocation: 'end',
      nameTextStyle: { color: getStyleProp('--grey-500'), fontWeight: 'bold' },
      splitLine: { show: false },
      axisLabel: {
        showMinLabel: false,
        showMaxLabel: false,
        color: getStyleProp('--grey-500'),
        fontWeight: 'bold',
        formatter: (value: any) => `${Math.abs(value)}`,
      },
    },
    series,
  } as EChartsOption;
};

type HpAggItem = { date: string; rawData?: any | null };

const hpSplitIntoWeeks = (dailyData: HpAggItem[]): HpAggItem[][] => {
  const weeks: HpAggItem[][] = [];
  let current: HpAggItem[] = [];
  dailyData.forEach((d, idx) => {
    const date = new Date(d.date);
    const dow = date.getDay(); // 0=Sun, 1=Mon, ...
    if (dow === 1 && current.length) { // Monday starts new week
      weeks.push(current);
      current = [];
    }
    current.push(d);
    if (idx === dailyData.length - 1 && current.length) weeks.push(current);
  });
  return weeks;
};

const hpSplitIntoHalfYear = (dailyData: HpAggItem[]): HpAggItem[][] => {
  const mid = Math.ceil(dailyData.length / 2);
  return [dailyData.slice(0, mid), dailyData.slice(mid)];
};

export const appBuildHeatPumpReducedEnergyAggregationCharts = (
  aggregatedData: { period: 'month' | 'year'; dailyData: HpAggItem[] },
  labels: Record<string, string>,
  energyAttrs: HpAttributeId[] = ['HP_E_EG', 'HP_T_EG']
): EChartsOption[] => {
  const isMonthMode = aggregatedData.period === 'month';
  const groups = isMonthMode
    ? hpSplitIntoWeeks(aggregatedData.dailyData)
    : hpSplitIntoHalfYear(aggregatedData.dailyData);

  return groups.map(group =>
    appBuildHeatPumpEnergyAggregationMultiBar(
      { period: aggregatedData.period, dailyData: group },
      labels,
      energyAttrs,
    )
  );
};
